<template>
  <v-dialog
    :key="renderKey"
    v-model="dialog"
    class="warehouse-select-dialog-mobile"
    fullscreen
    transition="dialog-bottom-transition">
    <v-card class="warehouse-select-card d-flex flex-column" color="mobileblue" tile>
      <div class="d-flex flex-column" style="min-height: 100dvh">
        <div class="px-2 py-1 d-flex align-center flex-nowrap flex-shrink-0 gap-2">
          <v-btn
            :aria-label="t('navigation.mobile.closeMenu')"
            class="flex-shrink-0"
            data-testid="warehouse-select-dialog-close"
            icon
            type="button"
            variant="text"
            @click="close">
            <v-icon color="white" size="24">mdi-close</v-icon>
          </v-btn>
          <v-icon class="flex-shrink-0" color="white" size="22">mdi-map-marker</v-icon>
          <strong
            class="text-white text-subtitle-2 font-weight-bold text-truncate"
            :title="t('appointment.dialogs.warehouseSelectDialog.headers.chooseWarehouse')">
            {{ t('appointment.dialogs.warehouseSelectDialog.headers.chooseWarehouse') }}
          </strong>
        </div>

        <v-divider class="opacity-30 my-0 flex-shrink-0" color="white" thickness="1" />

        <v-list
          class="warehouse-select-list flex-grow-1"
          bg-color="transparent"
          density="compact"
          lines="one"
          nav>
          <v-list-item
            v-for="warehouse in warehouses"
            :key="warehouse.id"
            :active="isWarehouseSelected(warehouse)"
            :disabled="!warehouse?.docks.length"
            class="warehouse-select-list-item"
            :ripple="true"
            @click="selectWarehouse(warehouse)">
            <v-list-item-title class="warehouse-select-list-item__title text-truncate">
              {{ warehouse.name }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { naturalSort } from '@nova/core';
import renderMixin from '@satellite/components/mixins/renderMixin';
import { useI18n } from 'vue-i18n';

export default {
  mixins: [renderMixin],
  props: {
    showDialog: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['close'],
  setup() {
    const { t } = useI18n();
    return { t };
  },
  data() {
    return {
      dialog: false,
      loading: false,
      warehouses: []
    };
  },
  methods: {
    close() {
      this.$emit('close');
      this.dialog = false;
      this.renderKey++;
    },
    async getData() {
      if (this.loading) {
        return this.warehouses;
      }
      this.loading = true;
      try {
        const warehouses = await this.services.warehouse.getWarehouses(
          {},
          {
            joins: ['docks||name,id,capacityChildren,isActive,loadTypeIds', 'org||name,settings']
          }
        );
        if (warehouses?.length > 0) {
          const data = naturalSort(warehouses, false, 'name');
          data.forEach(d => (d.org = this.$org));
          return data;
        }
      } catch (e) {
        console.error('Failed to load warehouses', e);
      } finally {
        this.loading = false;
      }
      return [];
    },
    isWarehouseSelected(warehouse) {
      return warehouse.id === this.$selectedWarehouse?.id;
    },
    selectWarehouse(warehouse) {
      this.$store.dispatch('Calendar/setSelectedWarehouse', warehouse);
      this.$store.commit(
        'Calendar/setSelectedDocks',
        this.util.getActiveDocksFromWarehouse(warehouse)
      );
      this.$store.commit(
        'Calendar/setSelectedLoadtypes',
        this.util.getLoadTypesFromActiveDocks(warehouse?.docks)
      );
      this.close();
    }
  },
  watch: {
    showDialog(val) {
      this.dialog = val;
    }
  },
  async mounted() {
    this.warehouses = await this.getData();
  }
};
</script>

<style lang="scss" scoped>
.warehouse-select-card {
  min-height: 100dvh;
}

/* Block layout (not flex column of buttons) so rows are only as tall as content. */
.warehouse-select-list {
  min-height: 0;
  overflow-y: auto;
}

.warehouse-select-list :deep(.v-list-item) {
  min-height: 36px !important;
  padding-top: 4px !important;
  padding-bottom: 4px !important;
  color: #ffffff !important;
  background-color: transparent !important;
}

.warehouse-select-list :deep(.v-list-item__content) {
  color: #ffffff !important;
}

.warehouse-select-list-item__title {
  color: #ffffff !important;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.4;
}

.warehouse-select-list :deep(.v-list-item--active .warehouse-select-list-item__title) {
  color: #ffe082 !important;
}

.warehouse-select-list :deep(.v-list-item--active) {
  background-color: rgba(255, 255, 255, 0.1) !important;
}
</style>
